"use client";

import { Check, Copy, Download, Globe, QrCode, Share } from "lucide-react";

import QRCode from "qrcode";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { toast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";
import { copyWithToast } from "@/lib/utils/clipboard";

interface ShareFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	formId: string | null;
	formSlug?: string | null;
	isPublished: boolean;
	onPublish: () => Promise<void>;
}

const QR_CODE_STYLE = {
	primaryColor: "#6366f1",
	backgroundColor: "#FFFFFF",
	logoSize: 32,
	cornerRadius: 6,
};

export function ShareFormModal({
	isOpen,
	onClose,
	formId,
	formSlug,
	isPublished,
	onPublish,
}: ShareFormModalProps) {

	const t = useTranslations("builder");	const [copying, setCopying] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
	const [downloading, setDownloading] = useState(false);
	const [showQr, setShowQr] = useState(false);
	const [generatingQr, setGeneratingQr] = useState(false);

	const shareUrl = formId
		? `${typeof window !== "undefined" ? window.location.origin : ""}/f/${formSlug || formId}`
		: "";

	useEffect(() => {
		if (isOpen && isPublished && shareUrl && showQr && !qrCodeDataUrl) {
			generateQrCode();
		}
	}, [isOpen, isPublished, shareUrl, showQr, qrCodeDataUrl]);

	const generateQrCode = async () => {
		if (!shareUrl) return;

		setGeneratingQr(true);
		try {
			const style = QR_CODE_STYLE;

			const qrDataUrl = await QRCode.toDataURL(shareUrl, {
				width: 256,
				margin: 4,
				errorCorrectionLevel: "M",
				color: {
					light: style.backgroundColor,
				},
			});

			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			if (!ctx) throw new Error("Could not get canvas context");

			canvas.width = 256;
			canvas.height = 256;

			const qrImage = new Image();
			qrImage.src = qrDataUrl;

			await new Promise((resolve, reject) => {
				qrImage.onload = resolve;
				qrImage.onerror = reject;
			});

			ctx.drawImage(qrImage, 0, 0, 256, 256);

			ctx.strokeStyle = style.primaryColor;
			ctx.lineWidth = 2;
			ctx.strokeRect(1, 1, 254, 254);

			setQrCodeDataUrl(canvas.toDataURL("image/png"));
		} catch (error) {
			console.error("Error generating QR code:", error);
			toast.error(t("failedGenerateQr"));
		} finally {
			setGeneratingQr(false);
		}
	};

	const handleCopyLink = async () => {
		if (!shareUrl) return;

		setCopying(true);
		// Запись в буфер запускается СРАЗУ, внутри жеста пользователя: браузер разрешает
		// её только пока живо окно активации (в Chrome ~5 с после клика, замерено).
		// Пока здесь стоял `await import("@/lib/utils/clipboard")`, первое нажатие после
		// каждой загрузки страницы тратило это окно на загрузку чанка — вместо ссылки
		// появлялась ошибка, и только второе нажатие (модуль уже загружен) срабатывало.
		const copyPromise = copyWithToast(
			shareUrl,
			t("linkCopied"),
			t("failedCopyLink")
		);

		try {
			await copyPromise;
		} catch (error) {
			console.error("Failed to copy link:", error);
			toast.error(t("failedCopyLink"));
		} finally {
			setTimeout(() => {
				setCopying(false);
			}, 2000);
		}
	};

	const handleDownloadQr = async () => {
		if (!qrCodeDataUrl) return;

		setDownloading(true);
		try {
			const link = document.createElement("a");
			link.download = `projme-qr-${formId}.png`;
			link.href = qrCodeDataUrl;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			toast.success(t("qrDownloaded"));
		} catch (error) {
			console.error("Failed to download QR code:", error);
			toast.error(t("failedDownloadQr"));
		} finally {
			setDownloading(false);
		}
	};

	const handlePublish = async () => {
		setPublishing(true);
		try {
			await onPublish();
		} catch (error) {
			console.error("Failed to publish form:", error);
		} finally {
			setPublishing(false);
		}
	};

	const handleToggleQr = () => {
		setShowQr(!showQr);
	};

	return (
		<Dialog onOpenChange={onClose} open={isOpen}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader className="flex flex-col gap-5">
					<DialogTitle className="flex items-center gap-2">
						<Share aria-hidden="true" className="size-5" />
						{t("shareForm")}
					</DialogTitle>
					<DialogDescription className="text-left">
						{isPublished
							? t("shareFormDesc")
							: t("publishToShare")}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-6">
					{isPublished ? (
						<>
							<div className="flex flex-col gap-3">
								<div className="flex items-center gap-2">
									<Globe
										aria-hidden="true"
										className="size-4 text-muted-foreground"
									/>
									<span className="font-medium text-sm">{t("shareLink")}</span>
								</div>
								<div className="flex gap-2">
									<Input
										className="flex-1 font-mono text-sm"
										id="share-url"
										readOnly
										value={shareUrl}
									/>
									<Button
										className="shrink-0"
										disabled={copying}
										onClick={handleCopyLink}
									>
										{copying ? (
											<>
												<Check className="size-4" />
												<span className="sr-only">{t("copied")}</span>
											</>
										) : (
											<>
												<Copy className="size-4" />
												<span className="sr-only">{t("copyLink")}</span>
											</>
										)}
									</Button>
								</div>
							</div>

							{}
							<div className="flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<Button onClick={handleToggleQr} size="sm" variant="outline">
										<div className="flex items-center gap-2">
											<QrCode
												aria-hidden="true"
												className="size-4 text-muted-foreground"
											/>
											<span className="font-medium text-sm">
												{showQr ? t("hide") : t("show")} {t("qrCode")}
											</span>
										</div>
									</Button>
								</div>

								{showQr && (
									<div className="rounded-lg border bg-muted/20 p-4">
										<div className="flex flex-col items-center gap-3">
											<div className="rounded-lg border bg-white p-3">
												{qrCodeDataUrl && !generatingQr ? (
													<img
														alt={t("qrCodeForForm")}
														className="size-24"
														src={qrCodeDataUrl}
													/>
												) : (
													<div className="flex size-24 items-center justify-center">
														{generatingQr ? (
															<div className="flex flex-col items-center gap-2">
																<div className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
																<span className="text-muted-foreground text-xs">
																	{t("generating")}
																</span>
															</div>
														) : (
															<QrCode className="size-6 text-muted-foreground" />
														)}
													</div>
												)}
											</div>
											<div className="text-center">
												<p className="mb-2 text-muted-foreground text-xs">
													{t("scanForMobile")}
												</p>
												<Button
													className="w-full"
													disabled={
														!qrCodeDataUrl || downloading || generatingQr
													}
													onClick={handleDownloadQr}
													size="sm"
													variant="outline"
												>
													<Download className="size-4" />
													{downloading ? t("downloading") : t("downloadQr")}
												</Button>
											</div>
										</div>
									</div>
								)}
							</div>
						</>
					) : (
						<div className="flex flex-col gap-4 text-center">
							<div className="rounded-lg bg-muted/50 p-6">
								<Globe
									aria-hidden="true"
									className="mx-auto mb-3 size-12 text-muted-foreground"
								/>
								<p className="text-muted-foreground text-sm">
									{t("publishNeeded")}
								</p>
							</div>
							<Button
								className="w-full"
								disabled={!formId || publishing}
								onClick={handlePublish}
							>
								{publishing ? t("publishing") : t("publishForm")}
							</Button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
